import { Container, Singleton } from 'typescript-ioc/es5'
import { action, computed, observable } from 'mobx'
import { create, persist } from 'mobx-persist'
import { SessionState } from '@pushly/aqe'
import { SidebarState } from './sidebar.state'
import { delay } from '@halo/_utils/delay'
import { DomainDto } from '@halo/dtos/domain.dto'
import { fetchAllDomains, fetchFlags } from '@halo/_utils/session-helpers'
import { AbilityStore } from '@halo/stores/ability.state'
import FlagList from '@halo/structs/flag-list'
import { PlatformUserDomainRecord } from '@halo/models/domains/platform-user-domain-record'

@Singleton
export class AppState {
    @observable
    public abilityStore: AbilityStore

    @observable
    public initialized: boolean = false

    @observable
    public loadingCount: any = 1

    @observable
    public moduleLoadingCount: number = 0

    @observable
    public showing404Count: number = 0

    public get moduleLoading(): boolean {
        return this.moduleLoadingCount !== 0
    }

    @persist
    @observable
    public locale: string = 'en'

    @persist
    @observable
    public displayTimeZone: string = 'UTC'

    public token?: string = undefined

    @observable
    public sidebar: SidebarState

    @observable
    public platformMessages: any[] = []

    public flags: FlagList = FlagList.from([])

    @persist
    @observable
    public userPlatformMessages: any

    @observable
    public currentUser?: any

    @observable
    public currentUserDomains?: PlatformUserDomainRecord[]

    @persist
    @observable
    public currentDomainJsonData?: any // persisted items have to be primitives

    private userDependencyFetcher: Promise<void> | undefined

    public get currentDomain(): any {
        return this.currentDomainJsonData
            ? DomainDto.fromApiResponse(JSON.parse(this.currentDomainJsonData))
            : this.currentDomainJsonData
    }

    @computed
    public get appIsLoading(): boolean {
        return this.loadingCount !== 0
    }

    @computed
    public get documentationLink(): string {
        return 'https://documentation.pushly.com'
    }

    public constructor() {
        this.sidebar = new SidebarState()
        this.abilityStore = new AbilityStore(this)
        this.rehydrate()
    }

    @action.bound
    public async fetchUserDependencies() {
        if (!this.userDependencyFetcher) {
            this.userDependencyFetcher = new Promise(async (res, rej) => {
                try {
                    const domainData = await fetchAllDomains({
                        query: {
                            fields: [
                                'id',
                                'name',
                                'display_name',
                                'status_id',
                                'account_id',
                                'account_name',
                                'account_flags',
                                'timezone',
                                'flags',
                                'default_icon_url',
                                'native_apns_configuration',
                                'native_fcm_configuration',
                            ],
                        },
                    })
                    this.currentUserDomains = domainData.data

                    if (!this.currentDomainJsonData) {
                        this.currentDomainJsonData = JSON.stringify(this.currentUserDomains![0])
                    } else if (this.currentDomain) {
                        const currentDomainUpdate = this.currentUserDomains!.find(
                            (domain) => domain.id === this.currentDomain!.id,
                        )
                        if (currentDomainUpdate) {
                            this.currentDomainJsonData = JSON.stringify(currentDomainUpdate)
                        } else {
                            this.currentDomainJsonData = JSON.stringify(this.currentUserDomains![0])
                        }
                    }
                } catch (err) {
                    rej(err)
                    return
                }

                // Start updating domain information in the background
                // poll time: 5 minutes
                const pollDelayMinutes = 5
                delay(() => this.fetchUserDependencies(), pollDelayMinutes * 60)

                res()

                this.userDependencyFetcher = undefined
            })
        }

        return this.userDependencyFetcher
    }

    private async rehydrate() {
        const hydrate = create({
            storage: localStorage,
            jsonify: true,
        })

        await hydrate('halo', this)
        await hydrate('halo.session', Container.get(SessionState))
        await hydrate('halo.sidebar', this.sidebar)

        const platformFlags = fetchFlags().then((flags) => (this.flags = flags))

        await platformFlags

        this.initialized = true
    }

    private unsetAppLoading() {
        this.loadingCount = this.loadingCount > 0 ? this.loadingCount - 1 : 0
    }
}
